import { Cross2Icon, ReloadIcon } from '@radix-ui/react-icons';
import { useQuery } from '@tanstack/react-query';
import React, { useId, useRef, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

import { CmsQueryKeys, fetchCmsAssetPreview, useUploadCmsAsset } from './cms-queries';

const SUPPORTED_IMAGE_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);

interface CmsImageAssetInputProps {
    cmsMicrositeGUID: string;
    assetGuid: string | undefined;
    disabled: boolean;
    label: string;
    altTextLabel?: string;
    captionLabel?: string;
    onChange: (assetGuid: string | undefined) => void;
}

export const CmsImageAssetInput = ({
    cmsMicrositeGUID,
    assetGuid,
    disabled,
    label,
    altTextLabel = 'Alt text',
    captionLabel,
    onChange,
}: CmsImageAssetInputProps) => {
    const uploadAssetMutation = useUploadCmsAsset();
    const inputId = useId();
    const inputRef = useRef<HTMLInputElement>(null);
    const [altText, setAltText] = useState('');
    const [caption, setCaption] = useState('');
    const [error, setError] = useState('');
    const assetPreviewQuery = useQuery({
        queryKey: CmsQueryKeys.assetPreview(assetGuid ?? ''),
        enabled: !!assetGuid,
        retry: false,
        queryFn: () => fetchCmsAssetPreview(assetGuid!),
    });

    const upload = async (file: File | undefined) => {
        setError('');
        if (!file) {
            return;
        }
        if (!SUPPORTED_IMAGE_TYPES.has(file.type)) {
            setError('Use a jpg, png, or webp file.');
            if (inputRef.current) {
                inputRef.current.value = '';
            }
            return;
        }

        const uploadedAssetGuid = await uploadAssetMutation.mutateAsync({
            cmsMicrositeGUID,
            altText: altText || undefined,
            caption: caption || undefined,
            fileToUpload: file as never,
        });
        if (uploadedAssetGuid) {
            onChange(uploadedAssetGuid);
        }
        if (inputRef.current) {
            inputRef.current.value = '';
        }
    };

    const remove = () => {
        setError('');
        onChange(undefined);
        if (inputRef.current) {
            inputRef.current.value = '';
        }
    };

    const pending = uploadAssetMutation.isPending;
    const missingCurrentAltText =
        !!assetGuid && assetPreviewQuery.isSuccess && !assetPreviewQuery.data.asset.altText?.trim();

    return (
        <div className="space-y-3">
            <div className="space-y-2">
                <Label htmlFor={`${inputId}-alt`}>{altTextLabel}</Label>
                <Input
                    id={`${inputId}-alt`}
                    value={altText}
                    disabled={disabled || pending}
                    className={
                        missingCurrentAltText
                            ? 'border-amber-400 bg-amber-50/40 focus-visible:ring-amber-400'
                            : undefined
                    }
                    onChange={(event) => setAltText(event.target.value)}
                />
            </div>
            {captionLabel ? (
                <div className="space-y-2">
                    <Label htmlFor={`${inputId}-caption`}>{captionLabel}</Label>
                    <Input
                        id={`${inputId}-caption`}
                        value={caption}
                        disabled={disabled || pending}
                        onChange={(event) => setCaption(event.target.value)}
                    />
                </div>
            ) : null}
            <div className="space-y-2">
                <Label htmlFor={`${inputId}-file`}>{assetGuid ? 'Replace image' : label}</Label>
                <Input
                    ref={inputRef}
                    id={`${inputId}-file`}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    disabled={disabled || pending}
                    onChange={(event) => upload(event.target.files?.[0])}
                />
            </div>
            <div className="flex items-center justify-between gap-2">
                {assetGuid ? (
                    <p className="text-muted-foreground min-w-0 text-xs break-all">Asset: {assetGuid}</p>
                ) : (
                    <span />
                )}
                {assetGuid ? (
                    <Button type="button" variant="ghost" size="xs" disabled={disabled || pending} onClick={remove}>
                        <Cross2Icon className="mr-1 h-3.5 w-3.5" /> Remove
                    </Button>
                ) : null}
            </div>
            {pending ? (
                <p className="text-muted-foreground flex items-center gap-1.5 text-xs">
                    <ReloadIcon className="h-3 w-3 animate-spin" /> Uploading
                </p>
            ) : null}
            {error ? <p className="text-destructive text-xs">{error}</p> : null}
        </div>
    );
};
